/**
 * Bottom thumbnail strip for multi-file navigation
 */

import { Box, Group } from '@mantine/core';
import { isImageFile, getFileIcon, getFileMimeColor } from '../../utils';
import type { ViewerFile } from '../../types';

interface ThumbnailStripProps {
  files: ViewerFile[];
  currentIndex: number;
  onSelect: (index: number) => void;
}

const THUMB_SIZE = 60;

export function ThumbnailStrip({ files, currentIndex, onSelect }: ThumbnailStripProps) {
  if (files.length <= 1) return null;

  return (
    <Box
      style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 10,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        backdropFilter: 'blur(8px)',
        padding: '8px 16px',
        overflowX: 'auto',
        overflowY: 'hidden',
        scrollbarWidth: 'thin',
      }}
    >
      <Group gap={8} wrap="nowrap" justify="center">
        {files.map((file, index) => {
          const isActive = index === currentIndex;
          const isImage = isImageFile(file.mimeType);

          return (
            <Box
              key={file.id || index}
              onClick={() => onSelect(index)}
              style={{
                width: THUMB_SIZE,
                height: THUMB_SIZE,
                minWidth: THUMB_SIZE,
                borderRadius: 6,
                overflow: 'hidden',
                cursor: 'pointer',
                border: isActive ? '2px solid white' : '2px solid transparent',
                transform: isActive ? 'scale(1.08)' : 'scale(1)',
                transition: 'all 0.15s ease',
                opacity: isActive ? 1 : 0.7,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: isImage ? 'transparent' : getFileMimeColor(file.mimeType),
              }}
            >
              {isImage && file.url ? (
                <img
                  src={file.variants?.[0]?.url || file.url}
                  alt={file.fileName}
                  loading="lazy"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                  draggable={false}
                />
              ) : (
                (() => {
                  const FileIcon = getFileIcon(file.mimeType);
                  return <FileIcon size={24} color="white" />;
                })()
              )}
            </Box>
          );
        })}
      </Group>
    </Box>
  );
}
